import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Building2, TrendingUp, AlertTriangle, DollarSign, CalendarClock, ArrowRight, PlusCircle, Sparkles, Package, CalendarDays } from "lucide-react";

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [props, setProps] = useState([]);
  const [ops, setOps] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data));
    api.get("/properties").then((r) => setProps(r.data));
    api.get("/operations/summary").then((r) => setOps(r.data)).catch(() => {});
  }, []);

  const s = summary || { properties_count: 0, avg_score: 0, at_risk_properties: 0, fine_risk_usd: 0, upcoming_deadlines: [] };

  return (
    <div className="space-y-8 animate-slide-up" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Overview</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 font-display">Portfolio compliance</h1>
        </div>
        <Link to="/app/properties" data-testid="add-property-cta" className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-lg text-sm hover:bg-slate-800 transition">
          <PlusCircle className="w-4 h-4" /> Add property
        </Link>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric icon={Building2} label="Properties" value={s.properties_count} testid="metric-properties" />
        <Metric icon={TrendingUp} label="Avg score" value={`${s.avg_score}%`} accent={s.avg_score >= 80 ? "emerald" : s.avg_score >= 50 ? "amber" : "rose"} testid="metric-score" />
        <Metric icon={AlertTriangle} label="At risk" value={s.at_risk_properties} accent={s.at_risk_properties > 0 ? "rose" : "emerald"} testid="metric-atrisk" />
        <Metric icon={DollarSign} label="Fine exposure" value={`$${(s.fine_risk_usd || 0).toLocaleString()}`} accent="amber" testid="metric-fine" />
      </div>

      {/* Operations at a glance */}
      {ops && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-semibold text-lg text-slate-900">Operations this week</h2>
            <Link to="/app/operations" className="text-sm text-sky-600 hover:underline flex items-center gap-1" data-testid="dash-ops-link">Manage <ArrowRight className="w-3 h-3" /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <OpTile icon={CalendarDays} label="Upcoming stays" value={ops.upcoming_stays} tone="sky" testid="ops-upcoming" />
            <OpTile icon={Sparkles} label="Turnovers (7d)" value={ops.turnovers_7d} tone="amber" testid="ops-turnovers" />
            <OpTile icon={AlertTriangle} label="Unassigned cleanings" value={ops.unassigned_cleanings} tone={ops.unassigned_cleanings > 0 ? "rose" : "slate"} testid="ops-unassigned" />
            <OpTile icon={Package} label="Low-stock items" value={ops.low_stock_items} tone={ops.low_stock_items > 0 ? "rose" : "slate"} testid="ops-lowstock" />
          </div>
        </div>
      )}

      {/* Deadlines + Properties */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-semibold text-lg text-slate-900">Your properties</h2>
            <Link to="/app/properties" className="text-sm text-sky-600 hover:underline flex items-center gap-1" data-testid="view-all-properties">View all <ArrowRight className="w-3 h-3" /></Link>
          </div>
          {props.length === 0 ? (
            <EmptyState msg="No properties yet. Add your first rental to see compliance status." cta="/app/properties" ctaLabel="Add property" testid="empty-properties" />
          ) : (
            <div className="space-y-3">
              {props.slice(0, 5).map((p) => (
                <Link to={`/app/properties/${p.id}`} key={p.id} data-testid={`dash-property-${p.id}`} className="flex items-center gap-4 p-3 rounded-lg border border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition">
                  <img src={p.hero_image} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 truncate">{p.city_name} · {p.address}</div>
                  </div>
                  <div className="text-right">
                    <ScoreBadge score={p.compliance?.score ?? 0} />
                    <div className="text-[10px] text-slate-500 mt-1 font-mono">{p.compliance?.compliant}/{p.compliance?.total} rules</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="font-display font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-slate-500" /> Upcoming
          </h2>
          {s.upcoming_deadlines.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No upcoming deadlines</div>
          ) : (
            <ul className="space-y-3">
              {s.upcoming_deadlines.map((d) => (
                <li key={d.id} className="border-l-2 pl-3" style={{ borderColor: d.days_until <= 7 ? "#DC2626" : d.days_until <= 30 ? "#D97706" : "#0284C7" }} data-testid={`deadline-${d.id}`}>
                  <div className="text-sm font-medium text-slate-900">{d.title}</div>
                  <div className="text-xs text-slate-500">{d.property_name}</div>
                  <div className="text-xs font-mono mt-0.5" style={{ color: d.days_until <= 7 ? "#DC2626" : "#475569" }}>
                    {d.days_until === 0 ? "Today" : d.days_until < 0 ? `${Math.abs(d.days_until)}d overdue` : `in ${d.days_until}d`}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, accent = "slate", testid }) {
  const map = { slate: "text-slate-500", emerald: "text-emerald-600", rose: "text-rose-600", amber: "text-amber-600" };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</span>
        <Icon className={`w-4 h-4 ${map[accent]}`} />
      </div>
      <div className="text-3xl font-bold text-slate-900 font-display">{value}</div>
    </div>
  );
}

function OpTile({ icon: Icon, label, value, tone = "slate", testid }) {
  const map = {
    slate: "border-slate-200 text-slate-600",
    sky: "border-sky-200 text-sky-700 bg-sky-50/60",
    amber: "border-amber-200 text-amber-700 bg-amber-50/60",
    rose: "border-rose-200 text-rose-700 bg-rose-50/60",
  };
  return (
    <div className={`border rounded-xl p-4 ${map[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-80">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-1.5 text-2xl font-display font-bold text-slate-900">{value}</div>
    </div>
  );
}

export function ScoreBadge({ score }) {
  const cls = score >= 80 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : score >= 50 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-rose-50 text-rose-700 border-rose-200";
  return <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{score}%</span>;
}

export function EmptyState({ msg, cta, ctaLabel, testid }) {
  return (
    <div className="py-10 text-center" data-testid={testid}>
      <div className="text-sm text-slate-500 mb-3">{msg}</div>
      {cta && <Link to={cta} className="inline-flex items-center gap-1 text-sm text-sky-600 hover:underline">{ctaLabel} <ArrowRight className="w-3 h-3" /></Link>}
    </div>
  );
}
